import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { actions } from 'react-redux-form';
import { Action, Dispatch } from 'redux';

import { AppState, timer } from '..';
import { LoginViewModel, ProfileViewModel } from '@models/Auth';
import { authService } from '@services/AuthService';
import { writeCookie } from '@common/cookies';
// import { getProfile } from '../profile/profile.actions';

import FormNames from '@constants/FormNames.enum';
import { JWT } from '@models/JWT';
import { config } from '@common/config';
import { Roles } from '@models/Roles';

/*
These are the actions for the page.
Think of these as 'event emitters' used by the components on the page.
*/

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum AuthActionType {
  LoginStart = 'LoginStart',
  LoginSuccess = 'LoginSuccess',
  LoginFailure = 'LoginFailure',
  LogoutStart = 'LogoutStart',
  LogoutSuccess = 'LogoutSuccess',
  LogoutFailure = 'LogoutFailure',
  TimerExpired = 'TimerExpired',
  TimerReset = 'TimerReset',
  TimerSet = 'TimerSet'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.
export interface LoginStartAction extends Action {
  readonly type: AuthActionType.LoginStart;
}

export interface LoginSuccessAction extends Action {
  readonly type: AuthActionType.LoginSuccess;
  readonly profile: ProfileViewModel;
}

export interface LoginFailureAction extends Action {
  readonly type: AuthActionType.LoginFailure;
  readonly error: string;
}

export interface LogoutStartAction extends Action {
  readonly type: AuthActionType.LogoutStart;
}

export interface LogoutSuccessAction extends Action {
  readonly type: AuthActionType.LogoutSuccess;
}

export interface LogoutFailureAction extends Action {
  readonly type: AuthActionType.LogoutFailure;
  readonly error: string;
}

export interface TimerExpiredAction extends Action {
  readonly type: AuthActionType.TimerExpired;
}

export interface TimerResetAction extends Action {
  readonly type: AuthActionType.TimerReset;
}

export interface TimerSetAction extends Action {
  readonly type: AuthActionType.TimerSet;
  readonly time: string;
}

// This is for handling an async action
export function login(model: LoginViewModel) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState();
    if (state.auth.isLoggingIn) {
      // if you're already logging in, don't do it again
      return;
    }
    dispatch(loginStart());
    try {
      const result = await authService.login(model.username, model.password);

      dispatch(loginSuccess(result.token));
      const newState: LoginViewModel = {
        username: model.rememberMe ? model.username : '',
        password: '',
        rememberMe: model.rememberMe
      };

      // getProfile();
      dispatch(actions.change(FormNames.LoginForm, newState));
    } catch (e) {
      dispatch(loginFailure(e.message));
    }
  };
}

// Actions for each action type declared here.
// They should all return an action object.
export function loginStart(): LoginStartAction {
  return {
    type: AuthActionType.LoginStart
  };
}

export function loginSuccess(token: string): LoginSuccessAction {
  // localStorage.setItem('token', result.token);
  if (config.environment === 'Local') {
    writeCookie('token', token);
  }

  var profileString = atob(token.split('.')[1]);
  writeCookie('profile', profileString);
  const jwt: JWT = JSON.parse(profileString);
  const profile: ProfileViewModel = {
    id: parseInt(jwt.jti, 0),
    email: jwt.sub,
    firstName: jwt.given_name,
    lastName: jwt.family_name,
    roles: jwt.roles ? (typeof jwt.roles === 'string' ? [jwt.roles] : jwt.roles) : [Roles.Admin],
    employeeId: jwt.nameid && jwt.nameid.length > 0 ? parseInt(jwt.nameid, 0) : undefined,
    mockedBy: jwt.mockedBy
  };

  return {
    type: AuthActionType.LoginSuccess,
    profile: profile
  };
}

export function loginFailure(error: string): LoginFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: AuthActionType.LoginFailure,
    error: error
  };
}

export const mockUser = (mockedUserId: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    var response = await authService.mockUser(mockedUserId);
    dispatch(loginSuccess(response.token));
  };
};

export const stopMocking = () => {
  return async (dispatch: Dispatch<AppState>) => {
    var response = await authService.stopMocking();
    dispatch(loginSuccess(response.token));
  };
};

export function logout() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    // const state = getState();
    //  dispatch(logoutStart()); this gets triggered within the logout function, due to the structure of that file
    try {
      await authService.logout();
      dispatch(logoutSuccess());
    } catch (e) {
      dispatch(logoutFailure(e));
    }
  };
}

export function logoutStart(): LogoutStartAction {
  return {
    type: AuthActionType.LogoutStart
  };
}

export function logoutSuccess(): LogoutSuccessAction {
  return {
    type: AuthActionType.LogoutSuccess
  };
}

export function logoutFailure(error: string): LogoutFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error} />, ToastOptions.Error);
  return {
    type: AuthActionType.LogoutFailure,
    error: error
  };
}

export function timerExpired(): TimerExpiredAction {
  return {
    type: AuthActionType.TimerExpired
  };
}

export function timerReset(): TimerResetAction {
  timer.reset();
  return {
    type: AuthActionType.TimerReset
  };
}

export function timerResetCallback(): TimerResetAction {
  return {
    type: AuthActionType.TimerReset
  };
}

export function timerSet(time: string): TimerSetAction {
  return {
    type: AuthActionType.TimerSet,
    time: time
  };
}

export function resetForm(formName: string) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(actions.reset(formName));
  };
}



// WEBPACK FOOTER //
// ./src/auth/auth.actions.tsx